/**
 * Round 18 A35 -- an excerpt with its directed passages marked in place.
 *
 * The excerpt printed a passage addressed to the Engine unmarked between
 * the charter's paragraphs; the only mark was a warning line further
 * down, under the fold. The detector normalises whitespace, so a passage
 * is matched word by word, across line breaks.
 */
import type { ReactNode } from "react";

/** [start, end) ranges of each passage in the text, in order, unmerged. */
export function passageRanges(text: string, passages: string[]): [number, number][] {
  const out: [number, number][] = [];
  for (const p of passages) {
    // A passage cut at the detector's limit ends in an ellipsis.
    const words = p.replace(/…$/, "").trim().split(/\s+/).filter(Boolean);
    if (words.length === 0) continue;
    const esc = words.map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
    const re = new RegExp(esc.join("\\s+"), "g");
    let m: RegExpExecArray | null;
    while ((m = re.exec(text)) !== null) {
      out.push([m.index, m.index + m[0].length]);
      if (m[0].length === 0) re.lastIndex++;
    }
  }
  out.sort((a, b) => a[0] - b[0]);
  // Drop overlaps: the first range wins.
  const kept: [number, number][] = [];
  for (const r of out)
    if (!kept.length || r[0] >= kept[kept.length - 1]![1]) kept.push(r);
  return kept;
}

export function PassageExcerpt({
  text,
  passages,
  testId,
}: {
  text: string;
  passages: string[];
  testId?: string;
}): JSX.Element {
  const ranges = passageRanges(text, passages);
  const parts: ReactNode[] = [];
  let at = 0;
  ranges.forEach(([s, e], i) => {
    if (s > at) parts.push(text.slice(at, s));
    parts.push(
      <mark
        key={i}
        data-testid={testId ? `${testId}-passage` : undefined}
        className="rounded-sm bg-rag-red/15 text-rag-red outline outline-1 outline-rag-red/40"
      >
        {text.slice(s, e)}
      </mark>,
    );
    at = e;
  });
  if (at < text.length) parts.push(text.slice(at));
  return (
    <blockquote
      data-testid={testId}
      data-marked={ranges.length}
      dir="auto"
      className="mt-1 max-h-48 overflow-y-auto whitespace-pre-wrap border-s-2 border-slate-300 ps-2 text-[11px] text-slate-700"
    >
      {parts}
    </blockquote>
  );
}
